import { clsx } from 'clsx'
import type { ComponentType, SVGProps } from 'react'
import { Link } from 'react-router-dom'
import { AlertErrorCircleFilled, CheckCircleFilled, XCircleFilled, XClear } from '@/assets/icons'
import { GlobalToastType } from '@/constants/constants-ui'
import { RootState } from '@/store/store'
import { closeGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { getLineClampClass } from './getLineClampClass'

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>

type VariantConfig = {
  id: string
  iconBgColor: string
  iconColor: string
  Icon: IconComponent
  iconLabel: string
}

const VARIANT_CONFIG: Record<GlobalToastType, VariantConfig> = {
  [GlobalToastType.SUCCESS]: {
    id: 'toast-success',
    iconBgColor: 'bg-green-100',
    iconColor: 'text-green-600',
    Icon: CheckCircleFilled,
    iconLabel: 'Success',
  },
  [GlobalToastType.ERROR]: {
    id: 'toast-danger',
    iconBgColor: 'bg-red-100',
    iconColor: 'text-red-600',
    Icon: XCircleFilled,
    iconLabel: 'Error',
  },
  [GlobalToastType.WARNING]: {
    id: 'toast-warning',
    iconBgColor: 'bg-orange-100',
    iconColor: 'text-orange-600',
    Icon: AlertErrorCircleFilled,
    iconLabel: 'Warning',
  },
}

/**
 * Slice-driven global toast (Success / Error / Warning).
 *
 * Mount once at the app root; dispatch `openGlobalToast()` to show one.
 * Single-slot — a new dispatch replaces the active toast (no queue).
 */
export default function GlobalToast() {
  // Store data
  const { open, message, type, durationMs, withLink, truncateAtLine } = useAppSelector((state: RootState) => state.globalToast)
  const dispatch = useAppDispatch()

  if (!open) return null

  // Close the toast after the duration
  setTimeout(() => {
    dispatch(closeGlobalToast())
  }, durationMs)

  const { id, iconBgColor, iconColor, Icon, iconLabel } = VARIANT_CONFIG[type]

  return (
    <div
      id={id}
      data-ds-component="GlobalToast"
      data-ds-variant={type}
      role="alert"
      aria-live="polite"
      className="fixed top-3 right-3 z-[1100] w-[320px] max-w-[calc(100vw-1.5rem)]"
    >
      <div className="flex items-center gap-4 rounded-lg bg-white border border-brand-neutral-200 p-4">
        <div className="flex flex-1 items-center gap-3 min-w-0">
          {/* Icon */}
          <div className={clsx('flex items-center justify-center shrink-0 p-[7px] rounded-lg', iconBgColor, iconColor)}>
            <Icon className="w-[18px] h-[18px]" aria-hidden="true" />
            <span className="sr-only">{iconLabel}</span>
          </div>

          {/* Message + optional link */}
          <div className="flex-1 min-w-0 break-words text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
            {withLink?.inline ? (
              <>
                <div className={clsx(getLineClampClass(truncateAtLine), 'inline')}>{message}</div>
                <Link
                  to={withLink.href}
                  onClick={() => dispatch(closeGlobalToast())}
                  className="ml-1 underline underline-offset-2 hover:text-brand-neutral-900"
                >
                  {withLink.label}
                </Link>
              </>
            ) : (
              <>
                <div className={getLineClampClass(truncateAtLine)}>{message}</div>
                {withLink && (
                  <Link
                    to={withLink.href}
                    onClick={() => dispatch(closeGlobalToast())}
                    className="mt-1 inline-block underline underline-offset-2 hover:text-brand-neutral-900"
                  >
                    {withLink.label}
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={() => dispatch(closeGlobalToast())}
          className="shrink-0 inline-flex items-center justify-center p-1 rounded-md text-brand-neutral-500 hover:bg-brand-neutral-100 focus:outline-none"
          aria-label="Close"
        >
          <span className="sr-only">Close</span>
          <XClear className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
